// src/pages/Home.jsx — Fully Mobile Responsive + Dynamic Content
import { SecTitle, Tag, Btn, GlowBg, MarqueeStrip } from "../components/ui/UI";
import { useState, useEffect, useRef, useCallback } from "react";
import { collection, getDocs, query, where, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";
import useSiteContent from "../hooks/useSiteContent";
import FeaturedCoursesSection from "../components/FeaturedCoursesSection";
import FeaturedContentCards from "../components/FeaturedContentCards";
import HeroOrbitVisual from "../components/home/HeroOrbitVisual";
import PortalIcon from "../components/ui/PortalIcon";

// ── CMS-driven sections ───────────────────────────────────────────────────────
function AnnouncementBar({ data, setPage, t }) {
  const [dismissed, setDismissed] = useState(false);
  if (!data?.enabled || !data?.text || dismissed) return null;
  const handleBtn = () => {
    if (data.buttonLink) setPage(data.buttonLink);
  };
  return (
    <div style={{
      background: "linear-gradient(90deg,#F59E0B,#F97316)",
      color: "#070B14", padding: "9px 16px",
      display: "flex", alignItems: "center", justifyContent: "center",
      gap: 12, flexWrap: "wrap", fontSize: ".84rem", fontWeight: 700, position: "relative",
    }}>
      <span>{data.text}</span>
      {data.buttonText && (
        <button onClick={handleBtn}
          style={{ padding: "4px 14px", borderRadius: 20, border: "2px solid rgba(7,11,20,.3)", background: "rgba(7,11,20,.12)", color: "#070B14", fontWeight: 800, fontSize: ".78rem", cursor: "pointer", fontFamily: "inherit" }}>
          {data.buttonText}
        </button>
      )}
      <button onClick={() => setDismissed(true)} aria-label="Dismiss"
        style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(7,11,20,.6)", fontSize: "1rem", lineHeight: 1, padding: "4px 6px" }}>
        ✕
      </button>
    </div>
  );
}

function TrustBadges({ badges, t }) {
  const active = (badges || []).filter(b => b.active);
  if (!active.length) return null;
  return (
    <section style={{ padding: "clamp(28px,4vw,44px) clamp(16px,5%,60px)", background: t.bgCard2 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, justifyContent: "center", maxWidth: 900, margin: "0 auto" }}>
        {active.map((b, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12, minWidth: 160, flex: "1 1 160px", maxWidth: 240 }}>
            <span style={{ fontSize: "1.5rem", flexShrink: 0 }}>{b.icon}</span>
            <div>
              <div style={{ fontWeight: 800, fontSize: ".88rem", color: t.text, lineHeight: 1.2 }}>{b.title}</div>
              {b.description && <div style={{ fontSize: ".74rem", color: t.muted, marginTop: 2 }}>{b.description}</div>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PromoBanner({ data, setPage, t }) {
  if (!data?.enabled || !data?.title) return null;
  return (
    <section style={{ padding: "clamp(36px,5vw,56px) clamp(16px,5%,60px)", background: t.bg }}>
      <div style={{
        maxWidth: 900, margin: "0 auto", textAlign: "center",
        background: `linear-gradient(135deg,rgba(245,158,11,.14),rgba(249,115,22,.08),rgba(99,102,241,.06))`,
        border: `1px solid rgba(245,158,11,.3)`, borderRadius: 20,
        padding: "clamp(28px,5vw,48px)", position: "relative", overflow: "hidden",
        boxShadow: "0 20px 60px rgba(245,158,11,.1)",
      }}>
        {data.badgeText && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 14px", background: "rgba(245,158,11,.15)", border: "1px solid rgba(245,158,11,.3)", borderRadius: 20, fontSize: ".76rem", fontWeight: 700, color: t.gold, marginBottom: 16 }}>
            {data.badgeText}
          </div>
        )}
        <h2 style={{ fontSize: "clamp(1.5rem,4vw,2.4rem)", fontWeight: 900, color: t.text, marginBottom: 12, lineHeight: 1.2 }}>{data.title}</h2>
        {data.subtitle && <p style={{ fontSize: "clamp(.88rem,2vw,1rem)", color: t.muted, marginBottom: 24, lineHeight: 1.65, maxWidth: 560, margin: "0 auto 24px" }}>{data.subtitle}</p>}
        {data.buttonText && (
          <button onClick={() => setPage(data.buttonLink || "bundles")}
            style={{ padding: "13px 32px", background: "linear-gradient(135deg,#F59E0B,#F97316)", color: "#070B14", border: "none", borderRadius: 12, fontWeight: 800, fontSize: ".95rem", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 12px 28px rgba(245,158,11,.3)" }}>
            {data.buttonText}
          </button>
        )}
      </div>
    </section>
  );
}

function FAQSection({ faqs, t }) {
  const active = (faqs || []).filter(f => f.active && f.question);
  const [open, setOpen] = useState(null);
  if (!active.length) return null;
  return (
    <section style={{ padding: "clamp(52px,7vw,72px) clamp(16px,5%,60px)", background: t.bgCard2 }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "clamp(28px,5vw,40px)" }}>
          <span style={{ display: "inline-block", padding: "5px 14px", borderRadius: 50, fontSize: ".72rem", fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 12, background: t.accentBg, color: t.accent }}>FAQ</span>
          <h2 style={{ fontSize: "clamp(1.75rem,3.2vw,2.4rem)", fontWeight: 800, color: t.text, lineHeight: 1.2 }}>Frequently Asked Questions</h2>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {active.map((faq, i) => (
            <div key={i} style={{ background: t.bgCard, border: `1px solid ${open === i ? t.gold : t.border}`, borderRadius: 12, overflow: "hidden", transition: "border-color .2s" }}>
              <button onClick={() => setOpen(open === i ? null : i)}
                style={{ width: "100%", padding: "16px 20px", background: "none", border: "none", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                <span style={{ fontWeight: 700, fontSize: ".93rem", color: t.text, lineHeight: 1.4 }}>{faq.question}</span>
                <span style={{ color: t.gold, fontSize: "1.1rem", fontWeight: 700, flexShrink: 0, transform: open === i ? "rotate(45deg)" : "rotate(0)", transition: "transform .2s" }}>+</span>
              </button>
              {open === i && (
                <div style={{ padding: "0 20px 16px", fontSize: ".9rem", color: t.muted, lineHeight: 1.7 }}>
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const CLIENTS = [
  "SVM Classes Official","MargDarshan Academy","Math By-Ranjeet Sir",
  "The Real Education","Prerna Classes","Osho Classes",
  "AECC Achievement English","G.P. Classes","Future Math",
  "Vishal Mathematics","G.S. Academy","Top Exam @ 100%",
  "Bright Way Institute","Vansh Biology","Srijan Chemistry",
  "Level Up English","Dreamset Classes","Maadhavi Results",
  "SDR Physics","Samarpan Online","Vidyalakshmi Classes",
  "Riyanshu Matric Art's","Maurya Classes","ARS Academy",
  "Mithila Commerce","+ More joining...",
];

const TESTIMONIALS = [
  { name:"SVM Classes Official", loc:"Bihar · 160+ orders", stars:5, quote:"Govardhan's work is absolutely professional. 12,000+ MCQs created — quality was outstanding. Best content partner!" },
  { name:"Math By-Ranjeet Sir",  loc:"Patna · Active Client", stars:5, quote:"4,600+ math questions — every single one accurate. My students now get the best material. Highly recommended!" },
  { name:"Vansh Biology",        loc:"Active Client", stars:5, quote:"268 biology questions at exactly the right level. Thumbnails were attractive too. Student response was excellent!" },
];

// ── Typing effect hook (looping) ─────────────────────────────────────────────
function useTypewriter(text, typeSpeed = 55, deleteSpeed = 30, pauseAfterType = 1800, pauseAfterDelete = 500) {
  const [displayed, setDisplayed] = useState("");
  const [phase, setPhase] = useState("waiting"); // waiting | typing | paused | deleting
  const timerRef = useRef(null);

  useEffect(() => {
    if (!text) return;
    let idx = 0;
    let current = "";
    let ph = "waiting";

    const clear = () => { if (timerRef.current) clearTimeout(timerRef.current); };

    const tick = () => {
      clear();
      if (ph === "waiting") {
        ph = "typing";
        setPhase("typing");
        timerRef.current = setTimeout(tick, 500);
      } else if (ph === "typing") {
        idx++;
        current = text.slice(0, idx);
        setDisplayed(current);
        if (idx >= text.length) {
          ph = "paused";
          setPhase("paused");
          timerRef.current = setTimeout(tick, pauseAfterType);
        } else {
          timerRef.current = setTimeout(tick, typeSpeed);
        }
      } else if (ph === "paused") {
        ph = "deleting";
        setPhase("deleting");
        timerRef.current = setTimeout(tick, 0);
      } else if (ph === "deleting") {
        idx--;
        current = text.slice(0, idx);
        setDisplayed(current);
        if (idx <= 0) {
          ph = "typing";
          setPhase("typing");
          timerRef.current = setTimeout(tick, pauseAfterDelete);
        } else {
          timerRef.current = setTimeout(tick, deleteSpeed);
        }
      }
    };

    tick();
    return clear;
  }, [text]);

  return { displayed, done: phase === "paused" };
}

// ── Animated counter hook ─────────────────────────────────────────────────────
function useCountUp(target, duration = 1400, startDelay = 600) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const num = parseFloat(String(target).replace(/[^0-9.]/g, "")) || 0;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      setTimeout(() => {
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          const ease = 1 - Math.pow(1 - p, 3);
          setVal(Math.round(ease * num));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }, startDelay);
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration, startDelay]);
  return { val, ref };
}

// ── Testimonial Slider ────────────────────────────────────────────────────────
function TestimonialSlider({ t, liveTestimonials }) {
  const items = liveTestimonials.length >= 1 ? liveTestimonials.slice(0, 6) : TESTIMONIALS;
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [animDir, setAnimDir] = useState("next"); // "next" | "prev"
  const total = items.length;
  const timerRef = useRef(null);

  const goTo = useCallback((idx, dir = "next") => {
    setAnimDir(dir);
    setActive((idx + total) % total);
  }, [total]);

  // Auto-play
  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(() => goTo(active + 1, "next"), 4000);
    return () => clearInterval(timerRef.current);
  }, [active, paused, goTo]);

  const prev = () => { clearInterval(timerRef.current); goTo(active - 1, "prev"); };
  const next = () => { clearInterval(timerRef.current); goTo(active + 1, "next"); };

  // Show 3 cards: prev (faded), active (full), next (faded)
  const getItem = (offset) => items[(active + offset + total) % total];

  return (
    <section
      style={{ padding:"clamp(60px,8vw,80px) clamp(16px,5%,60px)", background:t.bgCard2, overflow:"hidden" }}
      onMouseEnter={()=>setPaused(true)}
      onMouseLeave={()=>setPaused(false)}
    >
      <style>{`
        @keyframes slideInNext { from{opacity:0;transform:translateX(60px)} to{opacity:1;transform:translateX(0)} }
        @keyframes slideInPrev { from{opacity:0;transform:translateX(-60px)} to{opacity:1;transform:translateX(0)} }
        .tm-slide-next { animation: slideInNext .38s cubic-bezier(.25,.46,.45,.94) forwards; }
        .tm-slide-prev { animation: slideInPrev .38s cubic-bezier(.25,.46,.45,.94) forwards; }
        @media(max-width:700px){ .tm-side-card { display:none !important; } }
      `}</style>

      <SecTitle t={t} tag="Testimonials" h="What Our Clients Say" center/>

      {/* 3-column layout: left preview | main card | right preview */}
      <div style={{ display:"flex", gap:"clamp(10px,2vw,20px)", alignItems:"center", maxWidth:1100, margin:"0 auto 24px" }}>

        {/* Left preview card */}
        <div onClick={()=>prev()} className="tm-side-card"
          style={{
            flex:"0 0 clamp(150px,16vw,220px)", background:t.bgCard, border:`1px solid ${t.border}`,
            borderRadius:16, padding:"14px 14px", cursor:"pointer", opacity:.5,
            transition:"opacity .2s, transform .2s", userSelect:"none", alignSelf:"center",
            display:"flex", flexDirection:"column", justifyContent:"space-between",
            maxHeight:200,
          }}
          onMouseEnter={e=>{e.currentTarget.style.opacity=".85";e.currentTarget.style.transform="scale(1.02)";}}
          onMouseLeave={e=>{e.currentTarget.style.opacity=".5";e.currentTarget.style.transform="scale(1)";}}
        >
          <div>
            <div style={{ color:t.gold, fontSize:".72rem", letterSpacing:2, marginBottom:8 }}>{"★".repeat(getItem(-1).stars||5)}</div>
            <p style={{ fontSize:".8rem", color:t.muted, lineHeight:1.6, marginBottom:12,
              overflow:"hidden", display:"-webkit-box", WebkitLineClamp:3, WebkitBoxOrient:"vertical" }}>
              "{getItem(-1).quote}"
            </p>
          </div>
          <div style={{ fontWeight:700, fontSize:".78rem", color:t.text }}>{getItem(-1).name}</div>
        </div>

        {/* Main card */}
        <div style={{ flex:1, minWidth:0, position:"relative" }}>
          <div
            key={active}
            className={animDir === "next" ? "tm-slide-next" : "tm-slide-prev"}
            style={{
              background:t.bgCard, border:`1.5px solid ${t.gold}30`,
              borderRadius:20, padding:"clamp(22px,4vw,36px)",
              boxShadow:`0 20px 60px rgba(0,0,0,.18), 0 0 0 1px ${t.gold}12`,
              position:"relative", overflow:"hidden",
            }}
          >
            <div style={{ fontSize:"4rem", lineHeight:.8, color:`${t.gold}25`, fontFamily:"Georgia,serif", marginBottom:8, userSelect:"none" }}>"</div>
            <div style={{ color:t.gold, fontSize:"1rem", letterSpacing:3, marginBottom:14 }}>
              {"★".repeat(getItem(0).stars || 5)}
            </div>
            <p style={{ fontSize:"clamp(.92rem,2vw,1.05rem)", color:t.text, lineHeight:1.75, marginBottom:24, fontStyle:"italic" }}>
              "{getItem(0).quote}"
            </p>
            <div style={{ display:"flex", alignItems:"center", gap:14 }}>
              <div style={{
                width:48, height:48, borderRadius:"50%",
                background:`linear-gradient(135deg,${t.gold},#F97316)`,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontWeight:900, fontSize:"1.1rem", color:"#070B14", flexShrink:0,
              }}>
                {(getItem(0).name||"?")[0].toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight:800, fontSize:".95rem", color:t.text }}>{getItem(0).name}</div>
                <div style={{ fontSize:".78rem", color:t.muted, marginTop:2 }}>{getItem(0).loc}</div>
              </div>
            </div>
            {/* Progress bar */}
            <div style={{ position:"absolute", bottom:0, left:0, right:0, height:3, borderRadius:"0 0 20px 20px", background:t.border, overflow:"hidden" }}>
              <div key={`${active}-${paused}`} style={{
                height:"100%",
                background:`linear-gradient(90deg,${t.gold},#F97316)`,
                borderRadius:"inherit",
                animation: paused ? "none" : "tm-progress 4s linear forwards",
                width: paused ? "0%" : undefined,
              }}/>
            </div>
          </div>

          {/* Prev / Next arrows */}
          {[{side:"left",fn:prev},{side:"right",fn:next}].map(({side,fn})=>(
            <button key={side} onClick={fn} style={{
              position:"absolute", top:"50%", [side]: -18,
              transform:"translateY(-50%)",
              width:36, height:36, borderRadius:"50%",
              background:t.bgCard, border:`1.5px solid ${t.border}`,
              color:t.text, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
              boxShadow:"0 4px 16px rgba(0,0,0,.18)",
              transition:"all .18s", zIndex:2,
            }}
              onMouseEnter={e=>{e.currentTarget.style.background=t.gold;e.currentTarget.style.color="#070B14";}}
              onMouseLeave={e=>{e.currentTarget.style.background=t.bgCard;e.currentTarget.style.color=t.text;}}
            >
              {side==="left" ? "←" : "→"}
            </button>
          ))}
        </div>

        {/* Right preview card */}
        <div onClick={()=>next()} className="tm-side-card"
          style={{
            flex:"0 0 clamp(150px,16vw,220px)", background:t.bgCard, border:`1px solid ${t.border}`,
            borderRadius:16, padding:"14px 14px", cursor:"pointer", opacity:.5,
            transition:"opacity .2s, transform .2s", userSelect:"none", alignSelf:"center",
            display:"flex", flexDirection:"column", justifyContent:"space-between",
            maxHeight:200,
          }}
          onMouseEnter={e=>{e.currentTarget.style.opacity=".85";e.currentTarget.style.transform="scale(1.02)";}}
          onMouseLeave={e=>{e.currentTarget.style.opacity=".5";e.currentTarget.style.transform="scale(1)";}}
        >
          <div>
            <div style={{ color:t.gold, fontSize:".72rem", letterSpacing:2, marginBottom:8 }}>{"★".repeat(getItem(1).stars||5)}</div>
            <p style={{ fontSize:".8rem", color:t.muted, lineHeight:1.6, marginBottom:12,
              overflow:"hidden", display:"-webkit-box", WebkitLineClamp:3, WebkitBoxOrient:"vertical" }}>
              "{getItem(1).quote}"
            </p>
          </div>
          <div style={{ fontWeight:700, fontSize:".78rem", color:t.text }}>{getItem(1).name}</div>
        </div>

      </div>

      {/* Dot indicators */}
      <div style={{ display:"flex", justifyContent:"center", gap:8 }}>
        {items.map((_, i) => (
          <button key={i} onClick={()=>goTo(i, i>active?"next":"prev")} style={{
            width: i===active ? 24 : 8, height:8,
            borderRadius:999,
            background: i===active ? t.gold : `${t.gold}35`,
            border:"none", cursor:"pointer", padding:0,
            transition:"all .3s ease",
          }}/>
        ))}
      </div>
    </section>
  );
}

// ── Stat counter component ────────────────────────────────────────────────────
function StatCounter({ raw, label, t, delay = 800 }) {
  const suffix = String(raw || "").replace(/[0-9.]/g, ""); // "+" or "K+" etc.
  const { val, ref } = useCountUp(raw, 1400, delay);
  return (
    <div ref={ref}>
      <div style={{ fontSize:"clamp(1.4rem,4vw,2rem)", fontWeight:900, color:t.gold, lineHeight:1 }}>
        {val}{suffix}
      </div>
      <div style={{ fontSize:".78rem", color:t.muted, marginTop:3 }}>{label}</div>
    </div>
  );
}

export default function Home({ setPage, t }) {
  const { content } = useSiteContent();
  const { hero, general, services, marqueeItems } = content;
  const liveTestimonials  = content.testimonials || [];
  const announcement      = content.announcement || {};
  const promoBanner       = content.promoBanner  || {};
  const trustBadges       = content.trustBadges  || [];
  const faqs              = content.faqs         || [];

  // Typing on heading2
  const { displayed: typedText, done: typingDone } = useTypewriter(hero.heading2 || "Digital Partner", 60, 500);

  // Mouse spotlight
  const heroRef = useRef(null);
  const spotRef = useRef(null);
  const handleHeroMouseMove = useCallback((e) => {
    if (!heroRef.current || !spotRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    heroRef.current.style.setProperty("--mx", `${x}px`);
    heroRef.current.style.setProperty("--my", `${y}px`);
    spotRef.current.style.opacity = "1";
    spotRef.current.style.background = `
      radial-gradient(420px circle at ${x}px ${y}px, rgba(245,158,11,.22) 0%, rgba(249,115,22,.11) 34%, transparent 68%),
      radial-gradient(760px circle at ${x}px ${y}px, rgba(14,165,233,.08) 0%, transparent 58%),
      linear-gradient(110deg, transparent 0%, rgba(255,255,255,.055) 48%, transparent 62%)
    `;
  }, []);
  const handleHeroMouseLeave = useCallback(() => {
    if (spotRef.current) {
      spotRef.current.style.opacity = "0";
      spotRef.current.style.background = "transparent";
    }
  }, []);

  const marqueeList = String(marqueeItems || "").split("\n").filter(Boolean);

  const SERVICES = [
    { icon:"info", title:"MCQ / Question Bank",    desc:"Chapter-wise MCQs for every subject.",              price:services.mcqRate },
    { icon:"dashboard", title:"PPT Notes & Slides",      desc:"Visual-rich Smart Board presentations.",            price:services.pptRate },
    { icon:"edit", title:"Test Papers",             desc:"Board pattern objective & subjective.",             price:services.testPaperRate },
    { icon:"palette", title:"Thumbnail & Poster",      desc:"YouTube thumbnails & banners that get clicks.",     price:services.thumbnailRate },
    { icon:"keyboard", title:"Hindi/English Typing",   desc:"Fast professional typing — papers, notes, books.",  price:services.typingRate },

  ];

  // Dynamic SEO: update document title when seo.title is set
  useEffect(() => {
    const title = content.seo?.title;
    if (title) document.title = title;
  }, [content.seo?.title]);

  return (
    <div>
      {/* Announcement bar — CMS controlled */}
      <AnnouncementBar data={announcement} setPage={setPage} t={t} />

      {/* CSS for Auto-Scrolling Gallery + Responsive */}
      <style>
        {`
          @keyframes scrollUpGallery {
            0% { transform: translateY(0); }
            100% { transform: translateY(-50%); }
          }
          @keyframes scrollDownGallery {
            0% { transform: translateY(-50%); }
            100% { transform: translateY(0); }
          }
          @keyframes fadeUp {
            from { opacity:0; transform:translateY(28px); }
            to   { opacity:1; transform:translateY(0); }
          }
          @keyframes fadeIn {
            from { opacity:0; } to { opacity:1; }
          }
          @keyframes floatA {
            0%,100% { transform:translateY(0px) rotate(0deg); }
            50%     { transform:translateY(-18px) rotate(4deg); }
          }
          @keyframes floatB {
            0%,100% { transform:translateY(0px) rotate(0deg); }
            50%     { transform:translateY(14px) rotate(-3deg); }
          }
          @keyframes blink {
            0%,100% { opacity:1; } 50% { opacity:0; }
          }
          @keyframes shimmer {
            0%   { background-position: -200% center; }
            100% { background-position:  200% center; }
          }
          @keyframes badgePulse {
            0%,100% { box-shadow:0 0 0 0 rgba(245,158,11,.4); }
            50%     { box-shadow:0 0 0 8px rgba(245,158,11,0); }
          }
          @keyframes particleFloat {
            0%   { transform:translateY(0) translateX(0) scale(1); opacity:.6; }
            33%  { transform:translateY(-30px) translateX(12px) scale(1.1); opacity:.4; }
            66%  { transform:translateY(-15px) translateX(-8px) scale(.9); opacity:.7; }
            100% { transform:translateY(0) translateX(0) scale(1); opacity:.6; }
          }
          @keyframes heroAuroraDrift {
            0%,100% { transform:translate3d(-2%, -1%, 0) rotate(0deg) scale(1); opacity:.78; }
            50% { transform:translate3d(2%, 2%, 0) rotate(10deg) scale(1.04); opacity:1; }
          }
          @keyframes heroRailSweep {
            0% { transform:translateX(-28%) rotate(-12deg); opacity:.05; }
            45% { opacity:.18; }
            100% { transform:translateX(28%) rotate(-12deg); opacity:.05; }
          }
          @keyframes heroStarPulse {
            0%,100% { transform:scale(.78); opacity:.24; }
            50% { transform:scale(1.18); opacity:.75; }
          }
          .hero-bg-aurora,
          .hero-bg-rails,
          .hero-bg-vignette,
          .hero-bg-stars {
            position:absolute;
            inset:0;
            pointer-events:none;
          }
          .hero-bg-aurora {
            z-index:0;
            background:
              radial-gradient(circle at 78% 26%, rgba(99,102,241,.18), transparent 34%),
              radial-gradient(circle at 65% 62%, rgba(245,158,11,.20), transparent 34%),
              radial-gradient(circle at 18% 86%, rgba(249,115,22,.12), transparent 32%),
              conic-gradient(from 215deg at 66% 52%, transparent 0 20%, rgba(245,158,11,.10) 24% 34%, transparent 40% 60%, rgba(14,165,233,.10) 65% 75%, transparent 82% 100%);
            filter: blur(12px) saturate(125%);
            animation: heroAuroraDrift 12s ease-in-out infinite;
          }
          .hero-bg-rails {
            z-index:0;
            overflow:hidden;
            opacity:.72;
          }
          .hero-bg-rails::before,
          .hero-bg-rails::after {
            content:"";
            position:absolute;
            left:-16%;
            top:12%;
            width:132%;
            height:2px;
            background:linear-gradient(90deg, transparent, rgba(245,158,11,.42), rgba(255,255,255,.25), transparent);
            box-shadow:0 0 22px rgba(245,158,11,.28);
            animation:heroRailSweep 8s ease-in-out infinite;
          }
          .hero-bg-rails::after {
            top:72%;
            animation-delay:2.4s;
            opacity:.7;
          }
          .hero-bg-vignette {
            z-index:0;
            background:
              radial-gradient(circle at var(--mx, 72%) var(--my, 42%), rgba(255,255,255,.08), transparent 18%),
              linear-gradient(90deg, rgba(7,11,20,.12), transparent 48%, rgba(15,23,42,.34)),
              radial-gradient(circle at 50% 50%, transparent 48%, rgba(7,11,20,.38) 100%);
            mix-blend-mode: screen;
            opacity:.58;
          }
          .hero-bg-stars span {
            position:absolute;
            width:4px;
            height:4px;
            border-radius:50%;
            background:#F59E0B;
            box-shadow:0 0 18px rgba(245,158,11,.82);
            animation:heroStarPulse var(--dur) ease-in-out var(--delay) infinite;
            opacity:.55;
          }
          .fade-up {
            opacity:0;
            animation: fadeUp .7s ease forwards;
          }
          .hero-badge { animation: badgePulse 2.4s ease-in-out infinite; }
          @keyframes tm-progress { from{width:0%} to{width:100%} }
          .typing-cursor {
            display:inline-block;
            width:3px;
            height:.9em;
            background:linear-gradient(135deg,#F59E0B,#F97316);
            margin-left:3px;
            border-radius:2px;
            vertical-align:middle;
            animation: blink .7s step-end infinite;
          }
          .gallery-col {
            display: flex;
            flex-direction: column;
            width: 100%;
            animation-duration: var(--gallery-speed, 34s);
            animation-timing-function: linear;
            animation-iteration-count: infinite;
            will-change: transform;
          }
          .gallery-col:hover { animation-play-state: paused; }
          .gallery-img {
            transition: transform .35s ease, box-shadow .35s ease, border-color .35s ease;
          }
          .gallery-img:hover {
            transform: scale(1.025);
            z-index: 2;
          }
          .hero-gallery {
            position: relative;
          }
          .hero-gallery::before,
          .hero-gallery::after {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            height: 18px;
            z-index: 3;
            pointer-events: none;
          }
          .hero-gallery::before {
            top: 0;
            background: linear-gradient(to bottom, rgba(7,11,20,.34), transparent);
          }
          .hero-gallery::after {
            bottom: 0;
            background: linear-gradient(to top, rgba(7,11,20,.34), transparent);
          }
          .hero-text {
            transform: translateY(clamp(-64px, -5vh, -34px));
          }

          /* ── Responsive ── */
          @media (max-width: 768px) {
            .hero-section { padding-top: 12px !important; padding-bottom: 24px !important; align-items: flex-start !important; }
            .hero-grid { grid-template-columns: 1fr !important; }
            .hero-gallery { height: 260px !important; order: 2; margin-top: 8px; }
            .hero-text { order: 1; transform: none; }
            .hero-stats { grid-template-columns: repeat(2, 1fr) !important; }
            .hero-btns { flex-direction: column !important; }
            .hero-btns a, .hero-btns button { width: 100% !important; text-align: center !important; justify-content: center !important; }
          }
          @media (max-width: 480px) {
            .hero-section { padding-top: 8px !important; }
            .hero-gallery { height: 220px !important; border-radius: 14px !important; }
            .hero-stats { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
          }
        `}
      </style>

      {/* Hero + Marquee wrapper */}
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        
        {/* ── HERO ──────────────────────────────────────────────────────────── */}
        <section
          ref={heroRef}
          className="hero-section"
          onMouseMove={handleHeroMouseMove}
          onMouseLeave={handleHeroMouseLeave}
          style={{
            flex: 1,
            "--mx": "72%",
            "--my": "42%",
            position:"relative", overflow:"hidden",
            display:"flex", alignItems:"center",
            padding:"clamp(10px,1.6vw,20px) 5% clamp(22px,3vw,46px)",
            background:
              `linear-gradient(180deg, ${t.bg} 0%, ${t.bgCard2 || t.bgCard} 48%, ${t.bg} 100%)`,
          }}>
          {/* Spotlight overlay */}
          <div className="hero-bg-aurora" />
          <div className="hero-bg-rails" />
          <div className="hero-bg-vignette" />
          <div className="hero-bg-stars">
            {[
              ["9%","18%","4.8s","0s"], ["26%","24%","6.4s",".8s"], ["52%","13%","5.6s","1.4s"],
              ["86%","22%","7.2s",".3s"], ["11%","72%","6.8s","1.9s"], ["43%","48%","5.2s",".6s"],
              ["72%","82%","6s","1.1s"], ["92%","62%","5.7s","2.2s"], ["34%","86%","7.6s",".2s"],
            ].map(([left, top, dur, delay], i) => (
              <span key={i} style={{ left, top, "--dur": dur, "--delay": delay }} />
            ))}
          </div>
          <div ref={spotRef} style={{ position:"absolute", inset:0, zIndex:1, pointerEvents:"none", opacity:0, transition:"opacity .24s ease, background .08s ease", mixBlendMode:"screen" }} />
          <GlowBg color={t.accent} size={500} top={-100} right={-100} />
          <GlowBg color={t.gold}   size={300} bottom={-50} left={-50} />
          <div style={{
            position:"absolute", inset:0, zIndex:0,
            backgroundImage:`
              linear-gradient(${t.text}16 1px,transparent 1px),
              linear-gradient(90deg,${t.text}16 1px,transparent 1px),
              radial-gradient(circle at 50% 50%, rgba(245,158,11,.10), transparent 34%)
            `,
            backgroundSize:"48px 48px",
            maskImage:"radial-gradient(circle at 58% 50%, #000 0 58%, transparent 92%)",
          }}/>

          {/* Floating particles */}
          {[
            {w:10,h:10,top:"18%",left:"8%",delay:"0s",dur:"6s",anim:"floatA",op:.5},
            {w:6, h:6, top:"70%",left:"4%",delay:"1.2s",dur:"8s",anim:"floatB",op:.35},
            {w:14,h:14,top:"35%",left:"44%",delay:".6s",dur:"7s",anim:"floatA",op:.25},
            {w:8, h:8, top:"80%",left:"40%",delay:"2s",dur:"5.5s",anim:"floatB",op:.4},
            {w:5, h:5, top:"12%",left:"52%",delay:"1.8s",dur:"9s",anim:"floatA",op:.3},
          ].map((p,i)=>(
            <div key={i} style={{
              position:"absolute", borderRadius:"50%",
              width:p.w, height:p.h,
              top:p.top, left:p.left,
              background:`linear-gradient(135deg,#F59E0B,#F97316)`,
              opacity:p.op,
              animation:`${p.anim} ${p.dur} ease-in-out ${p.delay} infinite`,
              zIndex:1,
              pointerEvents:"none",
            }}/>
          ))}

          <div className="hero-grid" style={{
            position:"relative", zIndex:2, width:"100%",
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: "clamp(24px,4vw,48px)", alignItems: "center"
          }}>

            {/* LEFT SIDE: Text Content */}
            <div className="hero-text" style={{ minWidth: 0 }}>
              <div className="fade-up hero-badge" style={{
                display:"inline-flex", alignItems:"center", gap:8,
                background:"rgba(245,158,11,.12)", border:"1px solid rgba(245,158,11,.35)",
                borderRadius:50, padding:"6px 16px",
                fontSize:".8rem", fontWeight:700, color:t.gold, marginBottom:22,
                animationDelay:"0s",
              }}>
                ⭐ {hero.badge}
              </div>

              <h1 style={{
                fontSize:"clamp(2rem,6vw,4rem)", fontWeight:800,
                lineHeight:1.12, color:t.text, marginBottom:18,
                opacity:0, animation:"fadeUp .7s .15s ease forwards",
              }}>
                <span style={{ display:"block", opacity:0, animation:"fadeUp .6s .1s ease forwards" }}>{hero.heading1}</span>
                <span style={{ display:"block", minHeight:"1.15em" }}>
                  <span style={{ background:"linear-gradient(135deg,#F59E0B,#F97316,#F59E0B)", backgroundSize:"200% auto", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text", animation: typingDone ? "shimmer 3s linear infinite" : "none" }}>
                    {typedText}
                  </span>
                  {!typingDone && <span className="typing-cursor"/>}
                </span>
                <span style={{ display:"block", opacity:0, animation:"fadeUp .6s .8s ease forwards" }}>{hero.heading3}</span>
              </h1>

              <p className="fade-up" style={{
                fontSize:"clamp(.95rem,2.5vw,1.1rem)", lineHeight:1.75,
                color:t.muted, marginBottom:32, maxWidth:520, animationDelay:".9s",
              }}>
                {hero.subtext}
              </p>

              <div className="hero-btns fade-up" style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:clamp_mb(50), animationDelay:"1s" }}>
                <Btn href={`https://wa.me/${general.whatsapp}`} variant="primary" t={t}
                  style={{ fontSize:"clamp(.88rem,2vw,.95rem)" }}>
                  <PortalIcon name="phone" size={15} />Get Started on WhatsApp
                </Btn>
                <Btn onClick={()=>setPage("services")} variant="ghost" t={t}
                  style={{ fontSize:"clamp(.88rem,2vw,.95rem)" }}>
                  <PortalIcon name="search" size={15} />View Services
                </Btn>
              </div>

              {/* Stats — with count-up animation */}
              <div className="hero-stats fade-up" style={{
                display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:"clamp(14px,3vw,24px)",
                animationDelay:"1.1s",
              }}>
                {[
                  [hero.stat1, hero.stat1label],
                  [hero.stat2, hero.stat2label],
                  [hero.stat3, hero.stat3label],
                  [hero.stat4, hero.stat4label],
                ].map(([n,l], idx) => (
                  <StatCounter key={idx} raw={n} label={l} t={t} delay={1200 + idx * 150} />
                ))}
              </div>
            </div>

            <HeroOrbitVisual t={t} />

          </div>
        </section>

        {/* 🔥 Yahan Par Marquee daal diya, ye ab exact screen ke bottom par dikhega without scroll kiye */}
        <MarqueeStrip t={t} items={marqueeList}/>
      </div>

      {/* ── TRUST BADGES — CMS controlled ────────────────────────────────── */}
      <TrustBadges badges={trustBadges} t={t} />

      {/* ── SERVICES ──────────────────────────────────────────────────────── */}
      <section style={{ padding:"clamp(52px,7vw,78px) clamp(16px,5%,60px)", background:t.bg }}>
        <SecTitle t={t} tag="What We Do" h="Services That Power Your Coaching"
          sub="Everything your institute needs — content, design, digital — one trusted partner."/>
        <div style={{
          display:"grid",
          gridTemplateColumns:"repeat(auto-fill,minmax(min(100%,260px),1fr))",
          gap:18,
        }}>
          {SERVICES.map(({ icon, title, desc, price }) => (
            <div key={title} className="card-hover" style={{
              background:t.bgCard, border:`1px solid ${t.border}`,
              borderRadius:16, padding:"22px 18px",
            }}>
              <div style={{ width:48, height:48, borderRadius:12, background:t.accentBg, color:t.accent, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:14 }}><PortalIcon name={icon} size={23} /></div>
              <h3 style={{ fontWeight:700, fontSize:"1rem", color:t.text, marginBottom:7 }}>{title}</h3>
              <p className="line-clamp-2" style={{ fontSize:".87rem", color:t.muted, lineHeight:1.6, marginBottom:12, minHeight:"2.75em" }}>{desc}</p>
              <div style={{ paddingTop:10, borderTop:`1px solid ${t.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
                <span style={{ fontSize:".83rem", fontWeight:800, color:t.gold }}>{price}</span>
                <button onClick={()=>setPage("services")} style={{ border:`1px solid ${t.gold}45`, background:`${t.gold}12`, color:t.gold, borderRadius:999, padding:"6px 10px", fontSize:".72rem", fontWeight:900 }}>
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
        {/* Branding Store highlight card */}
        <div
          onClick={() => setPage("branding-store")}
          style={{
            marginTop: 18, cursor: "pointer",
            background: "linear-gradient(135deg,rgba(245,158,11,.10),rgba(249,115,22,.06))",
            border: "1px solid rgba(245,158,11,.28)", borderRadius: 16,
            padding: "18px 22px",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap",
            transition: "border-color .2s, box-shadow .2s",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(245,158,11,.55)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(245,158,11,.12)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(245,158,11,.28)"; e.currentTarget.style.boxShadow = "none"; }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
              <span style={{ fontSize: "1.3rem" }}>🎨</span>
              <span style={{ fontWeight: 900, fontSize: ".98rem", color: t.text }}>Branding Store</span>
              <span style={{ fontSize: ".62rem", padding: "2px 8px", borderRadius: 20, background: "rgba(245,158,11,.2)", color: "#F59E0B", fontWeight: 800 }}>NEW</span>
            </div>
            <div style={{ color: t.muted, fontSize: ".85rem", lineHeight: 1.5 }}>
              Custom branded PPT/PDF products — your logo, name & contact printed on every page.
            </div>
          </div>
          <button
            onClick={e => { e.stopPropagation(); setPage("branding-store"); }}
            style={{ padding: "9px 18px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#F59E0B,#F97316)", color: "#070B14", fontWeight: 900, fontSize: ".84rem", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}
          >
            Explore Branding Store →
          </button>
        </div>

        <div style={{ textAlign:"center", marginTop:32 }}>
          <Btn onClick={()=>setPage("services")} variant="ghost" t={t}>View All Services & Pricing →</Btn>
        </div>
      </section>

      {/* ── SOFTWARE LANDING BUTTON ────────────────────────────────────────────── */}
      <section style={{ padding:"clamp(36px,5vw,58px) clamp(16px,5%,60px)", background:t.bg }}>
        <div style={{
          textAlign:"center",
          maxWidth:"980px",
          margin:"0 auto",
          position:"relative",
          overflow:"hidden",
          borderRadius:22,
          border:`1px solid ${t.border}`,
          background:"linear-gradient(135deg,rgba(245,158,11,.12),rgba(99,102,241,.08),rgba(15,23,42,.12))",
          boxShadow:"0 24px 80px rgba(0,0,0,.22)",
          padding:"clamp(28px,5vw,48px)",
        }}>
          <div style={{ width:56, height:56, borderRadius:18, background:"linear-gradient(135deg,#F59E0B,#F97316)", color:"#070B14", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", boxShadow:"0 16px 34px rgba(245,158,11,.32)" }}>
            <PortalIcon name="play" size={26} stroke={2.5} />
          </div>
          <Tag t={t}>AI Software</Tag>
          <h2 style={{ fontSize:"clamp(1.8rem,4vw,3rem)", fontWeight:900, color:t.text, margin:"0 0 12px", lineHeight:1.12 }}>
            Smart Teaching Software for Coaching Centers
          </h2>
          <p style={{ fontSize:"clamp(.98rem,2vw,1.15rem)", color:t.muted, margin:"0 auto 22px", lineHeight:1.7, maxWidth:680 }}>
            Generate smart board PPTs, question papers, notes, and classroom content faster with DG HelpMate AI.
          </p>
          <div style={{ display:"flex", justifyContent:"center", gap:10, flexWrap:"wrap", marginBottom:28 }}>
            {["PPT Generator","Question Paper Tools","Teacher Friendly"].map(item => (
              <span key={item} style={{ display:"inline-flex", alignItems:"center", gap:7, padding:"8px 12px", borderRadius:999, background:"rgba(255,255,255,.04)", border:`1px solid ${t.border}`, color:t.muted, fontSize:".82rem", fontWeight:800 }}>
                <PortalIcon name="check" size={14} />{item}
              </span>
            ))}
          </div>
          <div style={{ display:"flex", gap:16, justifyContent:"center", flexWrap:"wrap" }}>
            <button
              onClick={() => setPage("landing")}
              style={{
                display:"inline-flex",
                alignItems:"center",
                justifyContent:"center",
                gap:"10px",
                fontWeight:900,
                textDecoration:"none",
                borderRadius:"14px",
                transition:"all 0.25s ease",
                cursor:"pointer",
                border:"none",
                fontFamily:"inherit",
                background:"linear-gradient(135deg,#F59E0B,#F97316)",
                color:"#070B14",
                padding:"15px 28px",
                fontSize:"1rem",
                boxShadow:"0 16px 34px rgba(245,158,11,.32)",
                minWidth:240
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-3px)";
                e.currentTarget.style.boxShadow = "0 20px 42px rgba(245,158,11,.46)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 16px 34px rgba(245,158,11,.32)";
              }}
            >
              <PortalIcon name="play" size={18} stroke={2.5} />Open Software Page
              <PortalIcon name="arrowLeft" size={18} style={{ transform:"rotate(180deg)" }} />
            </button>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ──────────────────────────────────────────────────── */}
      {/* <section style={{ padding:"clamp(60px,8vw,80px) clamp(16px,5%,60px)", background:t.bgCard2 }}>
        <SecTitle t={t} tag="Process" h="4 Simple Steps" center
          sub="WhatsApp se order karo — professional quality files receive karo."/>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(min(100%,200px),1fr))", gap:24 }}>
          {[
            ["1","WhatsApp Us","Topic, quantity, deadline batao. Instant quote milega."],
            ["2","Confirm Order","Payment advance ya delivery pe — tumhara choice."],
            ["3","We Work","Professional quality content within deadline."],
            ["4","Files Delivered","WhatsApp ya Client Portal pe — anytime access."],
          ].map(([n,title,desc]) => (
            <div key={n} style={{ textAlign:"center" }}>
              <div style={{ width:64, height:64, borderRadius:"50%", background:`linear-gradient(135deg,${t.accent},${t.gold})`, color:"#fff", fontSize:"1.4rem", fontWeight:900, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", boxShadow:`0 8px 24px ${t.accentBg}` }}>{n}</div>
              <h3 style={{ fontWeight:700, fontSize:"1rem", color:t.text, marginBottom:8 }}>{title}</h3>
              <p style={{ fontSize:".86rem", color:t.muted, lineHeight:1.55 }}>{desc}</p>
            </div>
          ))}
        </div>
      </section> */}
      <section style={{
        padding: "clamp(52px,7vw,70px) clamp(16px,5%,60px)",
        background: t.bgCard2
      }}>
        <SecTitle 
          t={t} 
          tag="Process" 
          h="4 Simple Steps" 
          center
          sub="Order via WhatsApp — receive professional quality files."
        />

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
          gap: 28,
          maxWidth: "1000px",
          margin: "0 auto",   // ✅ center whole grid
          justifyContent: "center"
        }}>
          {[
            ["chat","01","Share Requirement","Tell us subject, quantity, format, and deadline."],
            ["card","02","Confirm Order","Get quote, timeline, and payment details clearly."],
            ["edit","03","Production Starts","We create content, design, typing, or PPT work."],
            ["download","04","Delivery & Support","Receive files with revision/support through WhatsApp."],
          ].map(([icon,n,title,desc]) => (
            
            <div key={n} style={{
              textAlign: "left",
              padding: "20px",
              borderRadius: "16px",
              background: t.bgCard,
              border: `1px solid ${t.border}`,
              transition: "all 0.3s ease",
              cursor: "pointer",
              position:"relative",
              overflow:"hidden"
            }}
            onMouseEnter={e=>{
              e.currentTarget.style.transform="translateY(-6px) scale(1.03)";
              e.currentTarget.style.boxShadow="0 20px 40px rgba(0,0,0,0.5)";
            }}
            onMouseLeave={e=>{
              e.currentTarget.style.transform="translateY(0) scale(1)";
              e.currentTarget.style.boxShadow="none";
            }}
            >

              <div style={{ position:"absolute", right:14, top:12, fontSize:"2.7rem", lineHeight:1, fontWeight:900, color:t.text, opacity:.035 }}>{n}</div>
              {/* Icon Box */}
              <div style={{
                width: 46,
                height: 46,
                borderRadius: "13px",
                background: "linear-gradient(135deg,#F59E0B,#F97316)",
                boxShadow: "0 10px 24px rgba(245,158,11,.24)",
                color: "#070B14",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 16
              }}>
                <PortalIcon name={icon} size={22} stroke={2.4} />
              </div>

              {/* Title */}
              <h3 style={{
                fontWeight: 850,
                fontSize: "1.05rem",
                color: t.text,
                marginBottom: 8
              }}>
                {title}
              </h3>

              {/* Description */}
              <p style={{
                fontSize: ".86rem",
                color: t.muted,
                lineHeight: 1.6
              }}>
                {desc}
              </p>

            </div>
          ))}
        </div>
      </section>
      {/* ── PROMO BANNER — CMS controlled ────────────────────────────────── */}
      <PromoBanner data={promoBanner} setPage={setPage} t={t} />

      {/* ── CLIENTS ───────────────────────────────────────────────────────── */}
      <section style={{ padding:"clamp(60px,8vw,80px) clamp(16px,5%,60px)", background:t.bg }}>
        <SecTitle t={t} tag="Trusted By" h="500+ Coaching Institutes Trust Us"
          sub="From Bihar to Jharkhand — top institutes rely on DG HelpMate."/>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(min(100%,185px),1fr))", gap:10 }}>
          {CLIENTS.map((name,i) => (
            <div key={i} className="card-hover" style={{ background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:10, padding:"11px 14px", display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:7, height:7, borderRadius:"50%", background:i===CLIENTS.length-1?t.gold:t.green, flexShrink:0 }}/>
              <span style={{ fontSize:".85rem", fontWeight:600, color:i===CLIENTS.length-1?t.gold:t.text }}>{name}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── TESTIMONIALS ──────────────────────────────────────────────────── */}
      <TestimonialSlider t={t} liveTestimonials={liveTestimonials} />

      {/* ── FAQ — CMS controlled ──────────────────────────────────────────── */}
      <FAQSection faqs={faqs} t={t} />

      {/* ── NEWSLETTER ────────────────────────────────────────────────────── */}
      <NewsletterSection t={t} setPage={setPage}/>

      {/* ── DYNAMIC FEATURED COURSES ────────────────────────────────────────────────────── */}
      <FeaturedCoursesSection t={t} setPage={setPage} />

      {/* ── FEATURED CONTENT ────────────────────────────────────────────────────── */}
      <FeaturedContentCards t={t} setPage={setPage} />

      {/* ── STORE HUB CTA ─────────────────────────────────────────────────────── */}
      <StoreHubSection t={t} setPage={setPage} />
    </div>
  );
}


// ── Store Hub CTA Section ─────────────────────────────────────────────────────
function StoreHubSection({ t, setPage }) {
  const cards = [
    { page:"services",      icon:"clipboard", label:"Services",      desc:"MCQ, PPT, Test Papers & more",    color:"#6366F1" },
    { page:"bundles",       icon:"package",   label:"Bundles",       desc:"Ready-made content packs",        color:"#F59E0B" },
    { page:"materials",     icon:"folder",    label:"Materials",     desc:"Free & paid study resources",     color:"#10B981" },
    { page:"branding-store",icon:"palette",   label:"Branding Store",desc:"Custom branded PPT & PDF",        color:"#8B5CF6" },
  ];
  return (
    <section style={{ padding:"clamp(52px,7vw,72px) clamp(16px,5%,60px)", background:t.bg, borderTop:`1px solid ${t.border}` }}>
      <div style={{ maxWidth:1100, margin:"0 auto" }}>
        <div style={{ textAlign:"center", marginBottom:"clamp(28px,5vw,40px)" }}>
          <span style={{ display:"inline-block", padding:"5px 14px", borderRadius:50, fontSize:".72rem", fontWeight:700, letterSpacing:".1em", textTransform:"uppercase", marginBottom:12, background:`${t.gold}14`, color:t.gold }}>
            One Stop Shop
          </span>
          <h2 style={{ fontSize:"clamp(1.75rem,3.5vw,2.5rem)", fontWeight:900, color:t.text, lineHeight:1.2, marginBottom:10 }}>
            Explore DG HelpMate Store
          </h2>
          <p style={{ fontSize:"clamp(.88rem,2vw,.98rem)", color:t.muted, maxWidth:500, margin:"0 auto", lineHeight:1.65 }}>
            Services, bundles, materials aur custom branding — sab ek jagah milega.
          </p>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,220px),1fr))", gap:14, marginBottom:28 }}>
          {cards.map(({ page, icon, label, desc, color }) => (
            <button key={page} onClick={() => setPage(page)}
              style={{ background:t.bgCard, border:`1px solid ${t.border}`, borderRadius:16, padding:"20px 18px", cursor:"pointer", textAlign:"left", fontFamily:"inherit", transition:"all .2s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = `0 12px 32px ${color}20`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}>
              <div style={{ width:42, height:42, borderRadius:12, background:`${color}18`, color, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:12 }}>
                <PortalIcon name={icon} size={20}/>
              </div>
              <div style={{ fontWeight:800, fontSize:".93rem", color:t.text, marginBottom:4 }}>{label}</div>
              <div style={{ fontSize:".78rem", color:t.muted, lineHeight:1.45 }}>{desc}</div>
            </button>
          ))}
        </div>

        <div style={{ textAlign:"center" }}>
          <button onClick={() => setPage("store")}
            style={{ padding:"14px 36px", background:"linear-gradient(135deg,#F59E0B,#F97316)", color:"#070B14", border:"none", borderRadius:14, fontWeight:900, fontSize:"1rem", cursor:"pointer", fontFamily:"inherit", boxShadow:"0 12px 28px rgba(245,158,11,.28)", display:"inline-flex", alignItems:"center", gap:10 }}
            onMouseOver={e => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 16px 36px rgba(245,158,11,.4)"; }}
            onMouseOut={e => { e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="0 12px 28px rgba(245,158,11,.28)"; }}>
            <PortalIcon name="store" size={18}/>
            Visit Store — All Products
          </button>
        </div>
      </div>
    </section>
  );
}

function clamp_mb(v) { return v; }

function NewsletterSection({ t, setPage }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error | invalid

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmedEmail);
    if (!trimmedEmail || !emailOk) {
      setStatus("invalid");
      return;
    }

    setStatus("loading");
    try {
      await addDoc(collection(db, "newsletterSignups"), {
        email: trimmedEmail.toLowerCase(),
        subscribedAt: serverTimestamp(),
        source: "homepage",
      });
      setStatus("done");
      setEmail("");
    } catch (error) {
      console.error("Newsletter signup failed:", error);
      setStatus("error");
    }
  };

  return (
    <section style={{
      padding:"clamp(48px,7vw,72px) clamp(16px,5%,60px)",
      background: t.bgCard,
      borderTop:`1px solid ${t.border}`,
      borderBottom:`1px solid ${t.border}`,
    }}>
      <div style={{ maxWidth:600, margin:"0 auto", textAlign:"center" }}>
        <div style={{ width:54, height:54, borderRadius:16, background:`${t.gold}18`, color:t.gold, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px" }}><PortalIcon name="mail" size={27} /></div>
        <h2 style={{ fontSize:"clamp(1.4rem,3vw,2rem)", fontWeight:800, color:t.text, marginBottom:8 }}>
          Get Free Teaching Tips & Updates
        </h2>
        <p style={{ color:t.muted, fontSize:".95rem", marginBottom:24, lineHeight:1.6 }}>
          Join 500+ teachers getting weekly tips on MCQ writing, YouTube growth, and student engagement.
          No spam — unsubscribe anytime.
        </p>

        {status === "done" ? (
          <div style={{ background:"rgba(16,185,129,.1)", border:"1px solid rgba(16,185,129,.3)", borderRadius:14, padding:"18px 24px", color:"#10B981", fontWeight:700, fontSize:".95rem" }}>
            <PortalIcon name="check" size={15} style={{ marginRight:6 }} />You're subscribed! Watch your inbox for the next update.
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display:"flex", gap:10, maxWidth:440, margin:"0 auto", flexWrap:"wrap" }}>
            <input
              type="email" required value={email} onChange={e=>setEmail(e.target.value)}
              placeholder="Enter your email address"
              style={{ flex:1, minWidth:200, padding:"13px 16px", background:t.bg, border:`1.5px solid ${t.border}`, borderRadius:10, fontSize:".95rem", color:t.text, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}
            />
            <button type="submit" disabled={status==="loading"}
              style={{ padding:"13px 24px", background:"linear-gradient(135deg,#F59E0B,#F97316)", color:"#070B14", border:"none", borderRadius:10, fontWeight:800, fontSize:".95rem", cursor:status==="loading"?"not-allowed":"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
              {status==="loading" ? "Subscribing..." : "Subscribe Free →"}
            </button>
          </form>
        )}

        {status === "invalid" && (
          <p style={{ color:"#EF4444", fontSize:".85rem", marginTop:10 }}>Please enter a valid email address.</p>
        )}
        {status === "error" && (
          <p style={{ color:"#EF4444", fontSize:".85rem", marginTop:10 }}>
            Something went wrong. Please refresh and try again.
          </p>
        )}

        <p style={{ color:t.muted, fontSize:".75rem", marginTop:12 }}>
          <PortalIcon name="shield" size={13} style={{ marginRight:5 }} />We never sell your data. Read our <button onClick={()=>setPage("privacy")} style={{ background:"none", border:"none", color:t.accent, cursor:"pointer", fontSize:".75rem", padding:0, textDecoration:"underline" }}>Privacy Policy</button>.
        </p>
      </div>
    </section>
  );
}
